import { memo } from 'react';

import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import useSWRMutation from 'swr/mutation';

import { CasinoCard } from './casinoCard/CasinoCard';

import css from './DraggableCasinoCards.module.css';

import { casinoService } from 'v2/api/casinoService';
import { SortableCard } from 'v2/components/card/sortableCard/SortableCard';
import { useSortableList } from 'v2/hooks/useSortableList';
import { CasinoModel } from 'v2/types/CasinoModel';

interface DraggableCasinoCardsProps {
  casinos: CasinoModel[];
  onRevalidate: VoidFunction;
}

export const DraggableCasinoCards = memo<DraggableCasinoCardsProps>(({ casinos, onRevalidate }) => {
  const { items, itemIds, sensors, activeItem, onDragStart, onDragEnd, onDragCancel } = useSortableList<CasinoModel>(
    casinos,
    {
      onReorder: (next) => {
        trigger(next.map((item, i) => ({ id: item.id, order: i + 1 })));
      },
    }
  );

  const { trigger } = useSWRMutation('changeCasinosOrder', casinoService.changeCasinosOrder, {
    onSuccess: () => {
      onRevalidate();
    },
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={itemIds}>
        <div className={css.draggableCasinoCards}>
          {items.map((item) => (
            <SortableCard key={item.id} id={item.id}>
              <CasinoCard casino={item} onRevalidate={onRevalidate} />
            </SortableCard>
          ))}
        </div>
      </SortableContext>

      <DragOverlay adjustScale style={{ cursor: 'grabbing' }}>
        {activeItem && <CasinoCard casino={activeItem} onRevalidate={onRevalidate} />}
      </DragOverlay>
    </DndContext>
  );
});

DraggableCasinoCards.displayName = 'DraggableCasinoCards';
